import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@chakra-ui/react'
import { useState } from 'react'

import { useIsDark } from '../../hooks/useIsDark'
import { TriangleArrowIcon } from '../Icons/TriangleArrowIcon'

const getMonthList = (format: 'short' | 'long') => {
  const months = Array.from({ length: 12 }, (_, index) => {
    const date = new Date(2000, index)
    return date.toLocaleString('default', { month: format })
  })
  return months
}

const generateYearList = (range: number): string[] => {
  const currentYear = new Date().getFullYear()
  return [...Array(5).keys()].map(i => (currentYear - range + i).toString())
}

export type YearMonthSelectorProps = {
  currentMonth: number
  currentYear: number
  setCalendar: (date: Date) => void
  range?: number
}

export const YearMonthSelector = ({
  currentMonth,
  currentYear,
  setCalendar,
  range = 2,
}: YearMonthSelectorProps) => {
  const isDark = useIsDark()
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth)
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const yearList = generateYearList(range)
  const monthList = getMonthList('short')
  const currentYearIndex = yearList.indexOf(currentYear.toString())
  const openPopover = () => setIsOpen(true)
  const closePopover = () => setIsOpen(false)

  return (
    <Flex margin={0} align="center" justify="space-between">
      <Popover
        matchWidth
        gutter={0}
        isOpen={isOpen}
        onOpen={openPopover}
        onClose={closePopover}
      >
        <PopoverTrigger>
          <Button
            onClick={() => {
              setIsOpen(() => !isOpen)
            }}
            backgroundColor={
              isDark
                ? isOpen
                  ? 'presets.turquoise.80'
                  : 'transparent'
                : isOpen
                  ? 'presets.turquoise.20'
                  : 'presets.neutral.0'
            }
            color={isDark ? 'presets.neutral.0' : 'presets.neutral.70'}
            border={0}
            borderRadius={4}
            fontWeight={600}
            padding={1}
            fontSize="14px"
            _hover={{
              color: isDark ? 'presets.neutral.0' : 'presets.neutral.100',
              backgroundColor: isDark
                ? 'presets.turquoise.80'
                : 'presets.neutral.10',
            }}
          >
            {getMonthList('long')[currentMonth]}, {currentYear}{' '}
            <TriangleArrowIcon
              ml="4px"
              width="11px"
              color={
                isDark
                  ? 'presets.neutral.0'
                  : isOpen
                    ? 'presets.neutral.100'
                    : 'presets.neutral.70'
              }
              stroke={
                isDark
                  ? 'presets.neutral.0'
                  : isOpen
                    ? 'presets.neutral.100'
                    : 'presets.neutral.70'
              }
              transform={`${isOpen ? 'rotate(-90deg)' : 'rotate(90deg)'}`}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          mt="5px"
          minHeight="270px"
          overflow="hidden"
          width="196px"
          boxShadow={
            isDark
              ? '0 4px 24px 0 rgba(0, 0, 0, 0.24)'
              : '0px 6px 20px 0px rgba(0, 0, 0, 0.14)'
          }
          bg={isDark ? 'presets.neutral.100' : 'presets.neutral.0'}
          color={isDark ? 'presets.neutral.0' : 'presets.neutral.90'}
          border={isDark ? '1px solid' : undefined}
          borderColor={isDark ? 'presets.neutral.90' : undefined}
        >
          <PopoverBody padding={0} overflow="hidden">
            <Accordion defaultIndex={[currentYearIndex]} overflow="hidden">
              {yearList.map((year, accordionItem) => (
                <AccordionItem
                  key={accordionItem}
                  borderColor={isDark ? 'presets.neutral.90' : undefined}
                >
                  <Heading as="h2">
                    <AccordionButton
                      backgroundColor={
                        Number(year) === Number(selectedYear)
                          ? isDark
                            ? 'presets.turquoise.80'
                            : 'presets.turquoise.20'
                          : 'transparent'
                      }
                      color={
                        isDark ? 'presets.neutral.0' : 'presets.neutral.90'
                      }
                      onClick={() => {
                        setSelectedYear(Number(year))
                        setCalendar(
                          new Date(`${selectedMonth + 1}-01-${Number(year)}`),
                        )
                      }}
                      _hover={{
                        backgroundColor:
                          Number(year) === Number(selectedYear)
                            ? isDark
                              ? 'presets.turquoise.80'
                              : 'presets.turquoise.20'
                            : isDark
                              ? 'presets.turquoise.50'
                              : 'presets.neutral.10',
                      }}
                    >
                      <Box
                        as="span"
                        fontSize="12px"
                        flex="1"
                        textAlign="left"
                        fontWeight={
                          Number(year) === Number(selectedYear) ? 700 : 500
                        }
                      >
                        {year}
                      </Box>
                    </AccordionButton>
                  </Heading>
                  <AccordionPanel height="112px" width="196px" padding="4px">
                    <Grid templateColumns="repeat(4, 1fr)" px="7px">
                      {monthList.map((month, monthIndex) => {
                        const currentSelected = monthIndex === selectedMonth
                        return (
                          <GridItem
                            alignItems="center"
                            borderRadius="4px"
                            cursor="pointer"
                            display="flex"
                            fontSize="12px"
                            fontWeight={currentSelected ? 700 : 500}
                            justifyContent="center"
                            key={monthIndex}
                            margin="auto"
                            minH="32px"
                            minW="100%"
                            tabIndex={0}
                            backgroundColor={
                              currentSelected
                                ? isDark
                                  ? 'presets.turquoise.50'
                                  : 'presets.neutral.100'
                                : 'transparent'
                            }
                            color={
                              currentSelected || isDark
                                ? 'presets.neutral.0'
                                : 'presets.neutral.100'
                            }
                            onClick={() => {
                              setSelectedMonth(monthIndex)
                              setIsOpen(!isOpen)
                              setCalendar(
                                new Date(
                                  `${monthIndex + 1}-01-${selectedYear}`,
                                ),
                              )
                            }}
                            _hover={{
                              backgroundColor: currentSelected
                                ? isDark
                                  ? 'presets.turquoise.50'
                                  : 'presets.neutral.100'
                                : isDark
                                  ? 'presets.turquoise.50'
                                  : 'presets.neutral.20',
                              fontWeight: currentSelected ? 700 : 500,
                              color: 'presets.neutral.0',
                            }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                setSelectedMonth(monthIndex)
                              }
                            }}
                          >
                            {month}
                          </GridItem>
                        )
                      })}
                    </Grid>
                  </AccordionPanel>
                </AccordionItem>
              ))}
            </Accordion>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Flex>
  )
}
